
import React from 'react';
import { Notification, Language } from '../types';
import { TRANSLATIONS } from '../translations';
import { Bell, CheckCircle, AlertTriangle, Info, X } from 'lucide-react';

interface NotificationDropdownProps {
  notifications: Notification[];
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onClose: () => void;
  language: Language;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onClose,
  language
}) => {
  const t = TRANSLATIONS[language];

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success': return <CheckCircle size={16} className="text-emerald-400" />;
      case 'warning': return <AlertTriangle size={16} className="text-yellow-400" />;
      case 'error': return <AlertTriangle size={16} className="text-rose-400" />;
      default: return <Info size={16} className="text-blue-400" />;
    }
  };

  const getTypeStyle = (type: Notification['type']) => {
    switch (type) {
      case 'success': return 'bg-emerald-500/10 border-emerald-500/20';
      case 'warning': return 'bg-yellow-500/10 border-yellow-500/20';
      case 'error': return 'bg-rose-500/10 border-rose-500/20';
      default: return 'bg-slate-800 border-slate-700';
    }
  };

  return (
    <div className="absolute right-0 top-12 w-80 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2">
      <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-slate-900/95 backdrop-blur-sm">
        <h3 className="font-bold text-white flex items-center gap-2">
          <Bell size={16} /> {t.notifications}
        </h3>
        <div className="flex gap-2">
           <button 
             onClick={onMarkAllAsRead} 
             className="text-xs text-indigo-400 hover:text-indigo-300"
           >
             {t.markAllRead}
           </button>
        </div>
      </div>
      
      <div className="max-h-80 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            {t.noNotifications}
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {notifications.map(n => (
              <div 
                key={n.id} 
                onClick={() => onMarkAsRead(n.id)}
                className={`p-4 cursor-pointer hover:bg-slate-800 transition-colors ${n.read ? 'opacity-60' : 'bg-slate-800/30'}`}
              >
                <div className="flex gap-3">
                  <div className={`mt-1 w-6 h-6 rounded-full flex items-center justify-center border ${getTypeStyle(n.type)}`}>
                    {getIcon(n.type)}
                  </div>
                  <div>
                    <h4 className={`text-sm font-medium ${n.read ? 'text-slate-400' : 'text-white'}`}>{n.title}</h4>
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">{n.message}</p>
                    <p className="text-[10px] text-slate-500 mt-2">{n.date}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="p-2 border-t border-slate-800 text-center bg-slate-900">
         <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-300 py-1 w-full">
            {t.close}
         </button>
      </div>
    </div>
  );
};
